import { useCallback, useEffect, useRef, useState } from "react";
import { Users, Upload, Wand2, Loader2, Pencil, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { useOutletContext } from "react-router-dom";
import { useI18n } from "@/i18n";
import { api, fileUrl } from "@/lib/api";
import { PageHeader, Empty } from "@/components/common";
import { Field, Pick } from "@/app/tool/Options";
import { ConfirmDelete } from "@/app/Projects";

const CATS = ["face_upper", "neck", "ears", "hands", "fingers", "ankles", "full_body", "other"];

function ModelForm({ open, onOpenChange, model, onSaved }) {
  const { t, errMsg } = useI18n();
  const [f, setF] = useState({ name: "", category: "face_upper", notes: "" });
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    setF(model ? { name: model.name, category: model.category, notes: model.notes || "" } : { name: "", category: "face_upper", notes: "" });
    setFile(null); setPreview(null); setConsent(false);
  }, [open, model]);
  const pick = (fl) => {
    if (!fl) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(fl.type)) return toast.error(t("errors.UNSUPPORTED_FORMAT"));
    if (fl.size > 15 * 1024 * 1024) return toast.error(t("errors.FILE_TOO_LARGE"));
    setFile(fl); setPreview(URL.createObjectURL(fl));
  };
  const save = async () => {
    setBusy(true);
    try {
      if (model) {
        await api.patch(`/models/${model.id}`, f);
        if (file) { const fd = new FormData(); fd.append("file", file); fd.append("consent", consent); await api.put(`/models/${model.id}/image`, fd); }
      } else {
        const fd = new FormData();
        fd.append("file", file); fd.append("name", f.name); fd.append("category", f.category); fd.append("notes", f.notes); fd.append("consent", consent);
        await api.post("/models", fd);
      }
      onSaved(); onOpenChange(false);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const needsConsent = !model || file;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[var(--bg-1)] border-[var(--line)] max-h-[90vh] overflow-y-auto" data-testid="model-form-dialog">
        <DialogHeader><DialogTitle>{model ? t("common.edit") : t("models.upload")}</DialogTitle></DialogHeader>
        <button type="button" onClick={() => ref.current?.click()} className="rounded-xl border-2 border-dashed border-[var(--line)] h-48 overflow-hidden bg-white/5 grid place-items-center" data-testid="model-file-btn">
          {preview || model ? <img src={preview || fileUrl(model.thumb_file_id)} alt="" className="h-full w-full object-contain bg-white" /> : <span className="text-sm text-neutral-400 flex items-center gap-2"><Upload size={16} />{t("tool.dropHere")}</span>}
        </button>
        <input ref={ref} type="file" hidden accept="image/jpeg,image/png,image/webp" onChange={(e) => pick(e.target.files[0])} data-testid="model-file-input" />
        <Field label={t("models.name")}><Input value={f.name} maxLength={60} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="model-name-input" /></Field>
        <Field label={t("models.category")}><Pick value={f.category} onChange={(v) => setF({ ...f, category: v })} options={CATS.map((c) => [c, t(`opts.modelCat.${c}`)])} testid="model-category-select" /></Field>
        <Field label={`${t("models.notes")} (${t("common.optional")})`}><Textarea rows={2} maxLength={300} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="model-notes-input" /></Field>
        {needsConsent && (
          <label className="flex gap-3 text-xs text-neutral-300 cursor-pointer">
            <Checkbox checked={consent} onCheckedChange={(c) => setConsent(!!c)} data-testid="model-consent-checkbox" />
            <span>{t("models.consent")}</span>
          </label>
        )}
        <p className="text-[11px] text-neutral-500">{t("models.noTrain")}</p>
        <button onClick={save} disabled={busy || !f.name || (!model && !file) || (needsConsent && !consent)} className="btn-brand rounded-full py-2.5 font-semibold disabled:opacity-50 flex justify-center" data-testid="model-save-btn">
          {busy ? <Loader2 size={16} className="animate-spin" /> : t("common.save")}
        </button>
      </DialogContent>
    </Dialog>
  );
}

function AiModelDialog({ open, onOpenChange, onQueued }) {
  const { t, errMsg } = useI18n();
  const [f, setF] = useState({ name: "", preset: "female_natural", description: "" });
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try { const { data } = await api.post("/models/generate", f); onQueued(data.job); onOpenChange(false); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[var(--bg-1)] border-[var(--line)]" data-testid="ai-model-dialog">
        <DialogHeader><DialogTitle>{t("models.generateAi")}</DialogTitle></DialogHeader>
        <Field label={t("models.name")}><Input value={f.name} maxLength={60} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="ai-model-name-input" /></Field>
        <Field label={t("tool.preset")}><Pick value={f.preset} onChange={(v) => setF({ ...f, preset: v })} options={["female_natural", "female_editorial", "male_classic", "male_casual"].map((k) => [k, t(`opts.preset.${k}`)])} testid="ai-model-preset-select" /></Field>
        <Field label={`${t("models.aiDesc")} (${t("common.optional")})`}><Textarea rows={2} maxLength={300} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="ai-model-desc-input" /></Field>
        <p className="text-[11px] text-neutral-500">{t("models.aiNote")}</p>
        <button onClick={go} disabled={busy} className="btn-brand rounded-full py-2.5 font-semibold disabled:opacity-50" data-testid="ai-model-generate-btn">{t("tool.generate")}</button>
      </DialogContent>
    </Dialog>
  );
}

export default function Models() {
  const { t, errMsg } = useI18n();
  const { reloadEnt } = useOutletContext();
  const [items, setItems] = useState(null);
  const [form, setForm] = useState({ open: false, model: null });
  const [ai, setAi] = useState(false);
  const [pending, setPending] = useState(null);
  const load = useCallback(() => api.get("/models").then((r) => setItems(r.data.items)), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!pending) return;
    const h = setInterval(async () => {
      const { data } = await api.get(`/jobs/${pending.id}`);
      if (["completed", "failed", "canceled"].includes(data.status)) {
        clearInterval(h); setPending(null); load(); reloadEnt();
        if (data.status === "failed") toast.error(t("errors.GEN_FAILED"));
      }
    }, 3000);
    return () => clearInterval(h);
  }, [pending, load, reloadEnt, t]);
  const del = async (id) => { try { await api.delete(`/models/${id}`); load(); } catch (e) { toast.error(errMsg(e)); } };

  return (
    <div data-testid="models-page">
      <PageHeader title={t("models.title")} sub={t("models.sub")}>
        <div className="flex gap-2">
          <button onClick={() => setAi(true)} disabled={!!pending} className="rounded-full border border-[var(--line)] px-4 py-2 text-sm flex items-center gap-2 hover:border-neutral-500 disabled:opacity-50" data-testid="open-ai-model-btn">
            {pending ? <Loader2 size={15} className="animate-spin" /> : <Wand2 size={15} />}{t("models.generateAi")}
          </button>
          <button onClick={() => setForm({ open: true, model: null })} className="btn-brand rounded-full px-4 py-2 text-sm font-semibold flex items-center gap-2" data-testid="open-model-upload-btn"><Upload size={15} />{t("models.upload")}</button>
        </div>
      </PageHeader>
      <p className="text-xs text-neutral-500 mb-6">{t("models.consistency")}</p>
      {items && items.length === 0 && <Empty icon={Users} text={t("models.empty")} />}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
        {items?.map((m) => (
          <div key={m.id} className="panel overflow-hidden" data-testid={`model-card-${m.id}`}>
            <img loading="lazy" src={fileUrl(m.thumb_file_id)} alt={m.name} className="aspect-[4/5] object-cover w-full bg-white" />
            <div className="p-3">
              <p className="text-sm font-medium truncate">{m.name}</p>
              <p className="text-[11px] text-neutral-500">{t(`opts.modelCat.${m.category}`)}</p>
              <div className="flex justify-end gap-1 mt-1">
                <button onClick={() => setForm({ open: true, model: m })} className="p-1.5 text-neutral-400 hover:text-white" data-testid={`model-edit-${m.id}`} aria-label={t("common.edit")}><Pencil size={15} /></button>
                <button onClick={() => setForm({ open: true, model: m })} className="p-1.5 text-neutral-400 hover:text-white" data-testid={`model-replace-${m.id}`} aria-label={t("models.replace")}><RefreshCw size={15} /></button>
                <ConfirmDelete text={t("models.deleteConfirm")} onConfirm={() => del(m.id)} testid={`model-delete-${m.id}`} />
              </div>
            </div>
          </div>
        ))}
      </div>
      <ModelForm open={form.open} model={form.model} onOpenChange={(o) => setForm({ ...form, open: o })} onSaved={load} />
      <AiModelDialog open={ai} onOpenChange={setAi} onQueued={(j) => { setPending(j); reloadEnt(); }} />
    </div>
  );
}
